// absolute imports
import { fromPromise } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

// relative imports
import { captureSentryGenericError } from '../../../components/error-handling/sentry';
import { errorVar } from '../cache/reactiveVars';

/**
 * Normalize GraphQL error fields from top-level or extensions.
 * Backend error shapes vary (sometimes `message` lives only under `extensions`),
 * so we keep this extraction defensive.
 */
const getGraphQLErrorFields = (err) => {
  const extensions = err?.extensions;

  const code = err?.code ?? extensions?.code;

  // Try a handful of common backend shapes for user-facing message
  const message =
    err?.message ??
    extensions?.message ??
    extensions?.error?.message ??
    (typeof extensions?.error === 'string' ? extensions.error : null) ??
    extensions?.exception?.message ??
    (typeof extensions?.exception === 'string' ? extensions.exception : null) ??
    extensions?.response?.message ??
    extensions?.reason ??
    (typeof extensions?.details === 'string' ? extensions.details : null) ??
    extensions?.details?.message ??
    extensions?.details?.error ??
    null;

  const details =
    err?.details ??
    extensions?.details ??
    extensions?.error?.details ??
    extensions?.response ??
    null;

  const path = err?.path ?? extensions?.path;

  return { code, message, details, path };
};

let isRefreshing = false;
let pendingRequests = [];

// store concurrent requests after err instead of erroring
const addPendingRequest = (pendingRequest) => {
  pendingRequests.push(pendingRequest);
};

// resolve stored concurrent requests
const resolvePendingRequests = () => {
  pendingRequests.map((callback) => callback());
  pendingRequests = [];
};

// FOR ERROR LINK -> returns a value only if you want to retry an operation
const errorLink = (getAccessTokenSilently) =>
  onError(
    ({
      graphQLErrors,
      networkError,
      operation,
      forward,
      // eslint-disable-next-line consistent-return
    }) => {
      if (networkError) {
        // handle 401 as network err -> maybe consider auth directive for backend resolves in future
        if (networkError.statusCode === 401) {
          // retry request after refreshing token, set auth error if it fails
          if (!isRefreshing) {
            isRefreshing = true;
            // use fromPromise to transform token check promise to observable that
            // can be returned up the chain
            return fromPromise(
              getAccessTokenSilently().catch(() => {
                // catch error if token refresh fails
                resolvePendingRequests();
                isRefreshing = false;
                // throw up logout prompt due to token being invalid
                errorVar({
                  code: 401,
                  message: 'Invalid Token',
                  modalKey: 'TOKEN_EXPIRED',
                  type: 'network',
                });
                return forward(operation);
              }),
            ).flatMap(() => {
              resolvePendingRequests();
              isRefreshing = false;
              return forward(operation); // retry the op the token failed on
            });
          }
          // if already refreshing, return resolution for concurrent requests instead
          // of multiple failures
          return fromPromise(
            new Promise((resolve) => {
              addPendingRequest(() => resolve());
            }),
          ).flatMap(() => forward(operation));
        }
        // handle 408 timeout error with popup msg to user
        if (networkError.statusCode === 408) {
          errorVar({
            code: 408,
            message: 'Request timeout. Please try refreshing the page.',
            modalKey: 'GENERIC_ERROR',
            operation,
            type: 'network',
          });
        } else if (networkError.statusCode === 400) {
          // 400 can be variable coercion / bad request (Apollo Server 5 may send 400 for these)
          const firstError = networkError.result?.errors?.[0];
          const message =
            firstError?.message ??
            (typeof firstError?.extensions?.message === 'string'
              ? firstError.extensions.message
              : null) ??
            'Invalid request. Please check your input and try again.';
          errorVar({
            code: 400,
            message,
            modalKey: 'GENERIC_ERROR',
            operation,
            type: 'network',
          });
        } else {
          errorVar({
            code: networkError.statusCode,
            message: networkError.message,
            modalKey: 'GENERIC_ERROR',
            operation,
            type: 'network',
            contactSupport: true,
          });
        }
      }

      if (graphQLErrors) {
        graphQLErrors.forEach((err, i) => {
          const fields = getGraphQLErrorFields(err);
          const { code, message, path, details } = fields;
          const errMessage = err?.message ?? message ?? code;
          switch (errMessage) {
            case "Wallet's would exceed transaction limit":
              // throw global error for wallet transaction threshold flag
              errorVar({
                code: code || 500,
                message: message ?? errMessage ?? 'Request failed.',
                modalKey: 'OVER_TX_LIMIT',
                operation: path ? path[0] : (err.path && err.path[0]) || null,
                type: 'graphql',
                details: details
                  ? { ...details, __typename: 'ErrorDetails' }
                  : null,
              });
              break;
            default: {
              const first = graphQLErrors[0];
              const firstFields = getGraphQLErrorFields(first);
              const finalMessage =
                firstFields.message ??
                first?.message ??
                firstFields.code ??
                'Request failed.';
              errorVar({
                code: firstFields.code || 500,
                message: finalMessage,
                details: firstFields.details ?? first.details,
                operation:
                  firstFields.path?.[0] ??
                  (first.path && first.path[0]) ??
                  null,
                type: 'graphql',
              });
              break;
            }
          }

          // Always capture GraphQL errors for monitoring/triage
          captureSentryGenericError(err, {
            apolloOperationName: operation?.operationName,
            apolloOperationType: operation?.query?.definitions?.[0]?.kind,
            graphqlErrorIndex: i,
            graphqlErrorCode: fields.code,
            graphqlErrorPath: path ? path.join('.') : null,
            graphqlErrorMessage: fields.message,
          });
        });
      }
    },
  );

export default errorLink;
